import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, Spinner, Text, VStack, useBreakpointValue } from '@chakra-ui/react';
import { Document, Page } from 'react-pdf';
import { PDFDocument, rgb } from 'pdf-lib';
import {
  FaCheck,
  FaDownload,
  FaHandPaper,
  FaHighlighter,
  FaPaintBrush,
  FaSave,
  FaTrash,
  FaUndo
} from 'react-icons/fa';
import { API_BASE } from '../api';

const buildStorageKey = (storageKey, pdfUrl) =>
  `igm-pdf-annotations:${storageKey || pdfUrl || 'default'}`;

const getUserIdFromSession = () => {
  try {
    const raw = sessionStorage.getItem('user');
    const user = raw ? JSON.parse(raw) : null;
    const parsed = Number.parseInt(user?.id_pessoa || user?.id || 0, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch (error) {
    return null;
  }
};

const normalizeRect = (rect, containerRect) => {
  const left = rect.left - containerRect.left;
  const top = rect.top - containerRect.top;
  if (rect.width <= 0 || rect.height <= 0 || !containerRect.width || !containerRect.height) {
    return null;
  }

  return {
    leftPct: left / containerRect.width,
    topPct: top / containerRect.height,
    widthPct: rect.width / containerRect.width,
    heightPct: rect.height / containerRect.height
  };
};

const createEmptyPageEntry = () => ({
  highlights: [],
  strokes: []
});

const getPageEntry = (annotations, pageNumber) =>
  annotations[String(pageNumber)] || createEmptyPageEntry();

const createStrokePath = (points, width, height) => {
  if (!Array.isArray(points) || points.length === 0) {
    return '';
  }

  return points
    .map((point, index) => {
      const x = point.xPct * width;
      const y = point.yPct * height;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
};

const getDistanceBetweenPoints = (pointA, pointB) => {
  if (!pointA || !pointB) {
    return Number.POSITIVE_INFINITY;
  }

  const deltaX = pointA.xPct - pointB.xPct;
  const deltaY = pointA.yPct - pointB.yPct;
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
};

const HIGHLIGHT_COLORS = [
  { id: 'yellow', fill: 'rgba(255, 235, 59, 0.55)', pdf: rgb(1, 0.92, 0.23) },
  { id: 'green', fill: 'rgba(163, 230, 53, 0.42)', pdf: rgb(0.7, 0.9, 0.22) }
];

const DRAW_COLORS = [
  { id: 'red', stroke: 'rgba(211, 27, 41, 0.92)', pdf: rgb(0.83, 0.11, 0.16) },
  { id: 'blue', stroke: 'rgba(37, 99, 235, 0.94)', pdf: rgb(0.15, 0.39, 0.92) }
];

const getHighlightColor = (colorId) =>
  HIGHLIGHT_COLORS.find((item) => item.id === colorId) || HIGHLIGHT_COLORS[0];

const getDrawColor = (colorId) =>
  DRAW_COLORS.find((item) => item.id === colorId) || DRAW_COLORS[0];

const PdfOcrViewer = ({
  pdfUrl,
  pageNumber = 1,
  onDocumentLoad,
  viewerWidth,
  viewerHeight,
  storageKey,
  annotationMode = false,
  continuousMode = false
}) => {
  const [documentNumPages, setDocumentNumPages] = useState(0);
  const [pageMetrics, setPageMetrics] = useState({});
  const [activeTool, setActiveTool] = useState(annotationMode ? 'highlight' : 'none');
  const [annotations, setAnnotations] = useState({});
  const [isLoadedFromApi, setIsLoadedFromApi] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [selectionPreview, setSelectionPreview] = useState({ pageNumber: null, rects: [] });
  const [highlightColorId, setHighlightColorId] = useState(HIGHLIGHT_COLORS[0].id);
  const [drawColorId, setDrawColorId] = useState(DRAW_COLORS[0].id);
  const [activePageNumber, setActivePageNumber] = useState(pageNumber || 1);
  const viewerRef = useRef(null);
  const pageRefs = useRef({});
  const drawStateRef = useRef({ active: false, strokeId: null, pageNumber: null });
  const liveStrokePointsRef = useRef([]);
  const drawFrameRef = useRef(null);
  const selectionCommitTimerRef = useRef(null);
  const lastCommittedSelectionRef = useRef('');
  const mobileHighlightRef = useRef({
    active: false,
    pageNumber: null,
    items: new Map(),
    textParts: []
  });
  const measureRef = useRef(null);
  const [renderWidth, setRenderWidth] = useState(viewerWidth || 720);
  const [liveStrokeState, setLiveStrokeState] = useState({ pageNumber: null, path: '' });
  const localStorageKey = useMemo(
    () => buildStorageKey(storageKey, pdfUrl),
    [pdfUrl, storageKey]
  );
  const userId = useMemo(() => getUserIdFromSession(), []);
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const hasFixedViewerWidth = typeof viewerWidth === 'number' && Number.isFinite(viewerWidth);
  const viewerMaxWidth = hasFixedViewerWidth ? viewerWidth : null;

  useEffect(() => {
    setPageMetrics({});
    setSelectionPreview({ pageNumber: null, rects: [] });
    setActivePageNumber(pageNumber || 1);
    setDocumentNumPages(0);
  }, [pageNumber, pdfUrl]);

  useEffect(() => {
    setActiveTool(annotationMode ? (isMobile ? 'none' : 'highlight') : 'none');
  }, [annotationMode, isMobile, pdfUrl]);

  useEffect(() => {
    if (!continuousMode) {
      setActivePageNumber(pageNumber || 1);
    }
  }, [continuousMode, pageNumber]);

  useEffect(() => {
    const measureNode = measureRef.current;
    if (!measureNode) {
      return undefined;
    }

    const updateWidth = () => {
      const availableWidth = Math.floor(measureNode.getBoundingClientRect().width || viewerWidth || 720);
      const nextWidth = Math.max(
        260,
        hasFixedViewerWidth ? Math.min(viewerWidth, availableWidth) : availableWidth
      );
      setRenderWidth(nextWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(measureNode);
    return () => observer.disconnect();
  }, [hasFixedViewerWidth, viewerWidth]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(localStorageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      setAnnotations(parsed && typeof parsed === 'object' ? parsed : {});
    } catch (error) {
      setAnnotations({});
    }
  }, [localStorageKey]);

  useEffect(() => {
    if (!annotationMode || !userId || !storageKey) {
      setIsLoadedFromApi(true);
      return;
    }

    let isMounted = true;
    setIsLoadedFromApi(false);

    const loadRemoteAnnotations = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/pdf-annotations?id_pessoa=${userId}&document_key=${encodeURIComponent(storageKey)}`
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || 'Falha ao carregar anotacoes do PDF.');
        }

        if (!isMounted) {
          return;
        }

        const remoteAnnotations =
          data?.annotations && typeof data.annotations === 'object' ? data.annotations : {};

        if (Object.keys(remoteAnnotations).length > 0) {
          setAnnotations(remoteAnnotations);
        }
      } catch (error) {
        // Mantem cache local se a API falhar.
      } finally {
        if (isMounted) {
          setIsLoadedFromApi(true);
          setIsDirty(false);
          setSaveError('');
          setLastSavedAt(Date.now());
        }
      }
    };

    loadRemoteAnnotations();

    return () => {
      isMounted = false;
    };
  }, [annotationMode, storageKey, userId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(localStorageKey, JSON.stringify(annotations));
    } catch (error) {
      // Mantem as anotacoes em memoria se o cache falhar.
    }
  }, [annotations, localStorageKey]);

  const persistAnnotations = useCallback(
    async (nextAnnotations) => {
      if (!annotationMode || !userId || !storageKey) {
        setIsDirty(false);
        setSaveError('');
        setLastSavedAt(Date.now());
        return true;
      }

      setIsSaving(true);
      setSaveError('');

      try {
        const response = await fetch(`${API_BASE}/pdf-annotations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_pessoa: userId,
            document_key: storageKey,
            annotations: nextAnnotations
          })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || 'Falha ao salvar anotacoes do PDF.');
        }

        setIsDirty(false);
        setLastSavedAt(Date.now());
        return true;
      } catch (error) {
        setSaveError(error.message || 'Falha ao salvar anotacoes do PDF.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [annotationMode, storageKey, userId]
  );

  useEffect(() => {
    if (!annotationMode || !userId || !storageKey || !isLoadedFromApi || !isDirty) {
      return;
    }

    const timer = setTimeout(() => {
      persistAnnotations(annotations);
    }, 350);

    return () => clearTimeout(timer);
  }, [annotationMode, annotations, isDirty, isLoadedFromApi, persistAnnotations, storageKey, userId]);

  const handleDocumentLoadSuccess = useCallback((documentInfo) => {
    const pages = Number(documentInfo?.numPages || 0);
    setDocumentNumPages(pages);
    if (pages > 0 && activePageNumber > pages) {
      setActivePageNumber(1);
    }
    if (typeof onDocumentLoad === 'function') {
      onDocumentLoad(documentInfo);
    }
  }, [activePageNumber, onDocumentLoad]);

  const updatePageMetric = useCallback((targetPageNumber, updater) => {
    setPageMetrics((previous) => ({
      ...previous,
      [targetPageNumber]: {
        ...(previous[targetPageNumber] || {}),
        ...updater
      }
    }));
  }, []);

  const handlePageLoadSuccess = useCallback((targetPageNumber) => async (page) => {
    try {
      const viewport = page.getViewport({ scale: 1 });
      updatePageMetric(targetPageNumber, {
        viewport: { width: viewport.width, height: viewport.height },
        ocrLoading: false,
        ocrError: '',
        ocrData: null
      });

      const textContent = await page.getTextContent();
      const hasText = Boolean(textContent?.items && textContent.items.length > 0);
      updatePageMetric(targetPageNumber, { hasNativeText: hasText });

      if (!hasText) {
        updatePageMetric(targetPageNumber, { ocrLoading: true, ocrError: '' });
        const response = await fetch(
          `${API_BASE}/pdf-ocr?url=${encodeURIComponent(pdfUrl)}&page=${targetPageNumber}`
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || 'Falha ao gerar OCR.');
        }
        updatePageMetric(targetPageNumber, {
          ocrData: data,
          ocrLoading: false,
          ocrError: ''
        });
      }
    } catch (error) {
      updatePageMetric(targetPageNumber, {
        ocrLoading: false,
        ocrError: error.message || 'Falha ao gerar OCR.'
      });
    }
  }, [pdfUrl, updatePageMetric]);

  const pageNumbers = useMemo(() => {
    if (!documentNumPages) {
      return [pageNumber || 1];
    }
    if (!continuousMode) {
      return [pageNumber || 1];
    }
    return Array.from({ length: documentNumPages }, (_, index) => index + 1);
  }, [continuousMode, documentNumPages, pageNumber]);

  const effectiveActivePage = continuousMode
    ? activePageNumber || pageNumbers[0] || 1
    : pageNumber || 1;

  const getPageMetric = useCallback((targetPageNumber) => (
    pageMetrics[targetPageNumber] || {}
  ), [pageMetrics]);

  const getRenderHeight = useCallback((targetPageNumber) => {
    const pageMetric = getPageMetric(targetPageNumber);
    const ocrData = pageMetric.ocrData;
    const viewport = pageMetric.viewport;

    if (ocrData?.width && ocrData?.height && renderWidth) {
      return ocrData.height * (renderWidth / ocrData.width);
    }
    if (viewport?.width && viewport?.height && renderWidth) {
      return viewport.height * (renderWidth / viewport.width);
    }
    if (typeof viewerHeight === 'number') {
      return viewerHeight;
    }
    return 600;
  }, [getPageMetric, renderWidth, viewerHeight]);

  const getOcrScale = useCallback((targetPageNumber) => {
    const pageMetric = getPageMetric(targetPageNumber);
    if (!pageMetric.ocrData?.width || !renderWidth) {
      return 1;
    }
    return renderWidth / pageMetric.ocrData.width;
  }, [getPageMetric, renderWidth]);

  const currentPageEntry = useMemo(
    () => getPageEntry(annotations, effectiveActivePage),
    [annotations, effectiveActivePage]
  );
  const currentHighlights = useMemo(
    () => currentPageEntry.highlights || [],
    [currentPageEntry]
  );
  const currentStrokes = useMemo(
    () => currentPageEntry.strokes || [],
    [currentPageEntry]
  );
  const activeHighlightColor = useMemo(() => getHighlightColor(highlightColorId), [highlightColorId]);
  const activeDrawColor = useMemo(() => getDrawColor(drawColorId), [drawColorId]);

  const getPageElementFromNode = useCallback((node) => {
    if (!node) {
      return null;
    }
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    if (!element?.closest) {
      return null;
    }
    return element.closest('[data-pdf-page-number]');
  }, []);

  const getSelectableTextElementFromPoint = useCallback((clientX, clientY, pageEl) => {
    if (!document.elementsFromPoint || !pageEl) {
      return null;
    }

    const elements = document.elementsFromPoint(clientX, clientY);
    return elements.find((element) => {
      if (!(element instanceof HTMLElement) || !pageEl.contains(element)) {
        return false;
      }
      if (element.classList.contains('igm-ocr-word')) {
        return true;
      }
      return Boolean(element.closest('.react-pdf__Page__textContent span'));
    }) || null;
  }, []);

  const appendMobileHighlightElement = useCallback((element, targetPageNumber) => {
    const pageEl = pageRefs.current[targetPageNumber];
    if (!pageEl || !(element instanceof HTMLElement)) {
      return;
    }

    const targetElement = element.classList.contains('igm-ocr-word')
      ? element
      : element.closest('.react-pdf__Page__textContent span');

    if (!(targetElement instanceof HTMLElement)) {
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const containerRect = pageEl.getBoundingClientRect();
    const normalized = normalizeRect(rect, containerRect);
    if (!normalized) {
      return;
    }

    const signature = [
      normalized.leftPct.toFixed(4),
      normalized.topPct.toFixed(4),
      normalized.widthPct.toFixed(4),
      normalized.heightPct.toFixed(4)
    ].join(':');

    const gesture = mobileHighlightRef.current;
    if (gesture.items.has(signature)) {
      return;
    }

    gesture.items.set(signature, normalized);
    const textValue = (targetElement.textContent || '').trim();
    if (textValue) {
      gesture.textParts.push(textValue);
    }

    setSelectionPreview({
      pageNumber: targetPageNumber,
      rects: Array.from(gesture.items.values())
    });
  }, []);

  useEffect(() => {
    if (!annotationMode || isMobile) {
      return undefined;
    }

    const clearSelectionCommitTimer = () => {
      if (selectionCommitTimerRef.current) {
        window.clearTimeout(selectionCommitTimerRef.current);
        selectionCommitTimerRef.current = null;
      }
    };

    const getSelectionPayload = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return { pageNumber: null, rects: [] };
      }

      const pageEl = getPageElementFromNode(selection.anchorNode) || getPageElementFromNode(selection.focusNode);
      if (!pageEl) {
        return { pageNumber: null, rects: [] };
      }

      const detectedPageNumber = Number.parseInt(pageEl.getAttribute('data-pdf-page-number') || '', 10);
      if (!Number.isInteger(detectedPageNumber) || detectedPageNumber <= 0) {
        return { pageNumber: null, rects: [] };
      }

      const range = selection.getRangeAt(0);
      const rectList = Array.from(range.getClientRects());
      const containerRect = pageEl.getBoundingClientRect();

      const rects = rectList
        .filter((rect) => {
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          return (
            centerX >= containerRect.left &&
            centerX <= containerRect.right &&
            centerY >= containerRect.top &&
            centerY <= containerRect.bottom
          );
        })
        .map((rect) => normalizeRect(rect, containerRect))
        .filter(Boolean);

      return {
        pageNumber: detectedPageNumber,
        rects
      };
    };

    const buildSelectionSignature = (selectedText, payload) =>
      [
        payload.pageNumber || '',
        selectedText,
        payload.rects
          .map((rect) => (
            [
              rect.leftPct.toFixed(4),
              rect.topPct.toFixed(4),
              rect.widthPct.toFixed(4),
              rect.heightPct.toFixed(4)
            ].join(':')
          ))
          .join('|')
      ].join('::');

    const commitSelection = () => {
      if (activeTool !== 'highlight') {
        setSelectionPreview({ pageNumber: null, rects: [] });
        lastCommittedSelectionRef.current = '';
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        setSelectionPreview({ pageNumber: null, rects: [] });
        lastCommittedSelectionRef.current = '';
        return;
      }

      const selectedText = selection.toString().trim();
      if (!selectedText) {
        setSelectionPreview({ pageNumber: null, rects: [] });
        lastCommittedSelectionRef.current = '';
        return;
      }

      const payload = getSelectionPayload();
      if (!payload.pageNumber || !payload.rects.length) {
        setSelectionPreview({ pageNumber: null, rects: [] });
        return;
      }

      const signature = buildSelectionSignature(selectedText, payload);
      if (lastCommittedSelectionRef.current === signature) {
        return;
      }

      const nextHighlight = {
        id: `pdf_h_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        createdAt: Date.now(),
        color: highlightColorId,
        text: selectedText.slice(0, 500),
        rects: payload.rects
      };

      setAnnotations((previous) => {
        const pageKey = String(payload.pageNumber);
        const pageEntry = getPageEntry(previous, payload.pageNumber);
        return {
          ...previous,
          [pageKey]: {
            ...pageEntry,
            highlights: [...pageEntry.highlights, nextHighlight]
          }
        };
      });
      setActivePageNumber(payload.pageNumber);
      setIsDirty(true);
      setSaveError('');
      setSelectionPreview({ pageNumber: null, rects: [] });
      lastCommittedSelectionRef.current = signature;
      selection.removeAllRanges();
    };

    const scheduleSelectionCommit = (delay = 120) => {
      clearSelectionCommitTimer();
      selectionCommitTimerRef.current = window.setTimeout(() => {
        selectionCommitTimerRef.current = null;
        commitSelection();
      }, delay);
    };

    const handleSelectionChange = () => {
      if (activeTool !== 'highlight') {
        setSelectionPreview({ pageNumber: null, rects: [] });
        clearSelectionCommitTimer();
        lastCommittedSelectionRef.current = '';
        return;
      }

      const payload = getSelectionPayload();
      setSelectionPreview(payload);
      if (payload.pageNumber) {
        setActivePageNumber(payload.pageNumber);
      }

      const currentSelection = window.getSelection();
      const selectedText = currentSelection?.toString().trim() || '';
      if (payload.pageNumber && payload.rects.length > 0 && selectedText) {
        scheduleSelectionCommit(160);
      } else {
        clearSelectionCommitTimer();
        if (!selectedText) {
          lastCommittedSelectionRef.current = '';
        }
      }
    };

    const handleSelectionEnd = () => {
      if (activeTool !== 'highlight') {
        setSelectionPreview({ pageNumber: null, rects: [] });
        clearSelectionCommitTimer();
        lastCommittedSelectionRef.current = '';
        return;
      }

      scheduleSelectionCommit(220);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleSelectionEnd);
    document.addEventListener('touchend', handleSelectionEnd);
    return () => {
      clearSelectionCommitTimer();
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleSelectionEnd);
      document.removeEventListener('touchend', handleSelectionEnd);
    };
  }, [activeTool, annotationMode, getPageElementFromNode, highlightColorId, isMobile]);

  const updatePage = useCallback((targetPageNumber, updater) => {
    setAnnotations((previous) => {
      const pageKey = String(targetPageNumber);
      const pageEntry = getPageEntry(previous, targetPageNumber);
      return {
        ...previous,
        [pageKey]: updater(pageEntry)
      };
    });
    setActivePageNumber(targetPageNumber);
    setIsDirty(true);
    setSaveError('');
  }, []);

  const updateCurrentPage = useCallback((updater) => {
    updatePage(effectiveActivePage, updater);
  }, [effectiveActivePage, updatePage]);

  const getPointFromEvent = useCallback((event, targetPageNumber) => {
    const pageEl = pageRefs.current[targetPageNumber];
    if (!pageEl) {
      return null;
    }
    const rect = pageEl.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return null;
    }

    return {
      xPct: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      yPct: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
    };
  }, []);

  const handleDrawPointerDown = useCallback((targetPageNumber) => (event) => {
    if (activeTool !== 'draw') {
      return;
    }

    event.preventDefault();
    setActivePageNumber(targetPageNumber);
    if (event.currentTarget?.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Ignora navegadores sem suporte completo.
      }
    }

    const point = getPointFromEvent(event, targetPageNumber);
    if (!point) {
      return;
    }

    const strokeId = `pdf_s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    drawStateRef.current = { active: true, strokeId, pageNumber: targetPageNumber };
    liveStrokePointsRef.current = [point];
    setLiveStrokeState({
      pageNumber: targetPageNumber,
      path: createStrokePath([point], renderWidth || 720, getRenderHeight(targetPageNumber))
    });
  }, [activeTool, getPointFromEvent, getRenderHeight, renderWidth]);

  const handleDrawPointerMove = useCallback((targetPageNumber) => (event) => {
    if (
      activeTool !== 'draw' ||
      !drawStateRef.current.active ||
      drawStateRef.current.pageNumber !== targetPageNumber
    ) {
      return;
    }

    event.preventDefault();
    const point = getPointFromEvent(event, targetPageNumber);
    if (!point) {
      return;
    }

    const points = liveStrokePointsRef.current;
    const lastPoint = points[points.length - 1];
    if (getDistanceBetweenPoints(lastPoint, point) < 0.0035) {
      return;
    }

    points.push(point);

    if (drawFrameRef.current) {
      return;
    }

    drawFrameRef.current = window.requestAnimationFrame(() => {
      drawFrameRef.current = null;
      setLiveStrokeState({
        pageNumber: targetPageNumber,
        path: createStrokePath(points, renderWidth || 720, getRenderHeight(targetPageNumber))
      });
    });
  }, [activeTool, getPointFromEvent, getRenderHeight, renderWidth]);

  const resetMobileHighlightGesture = useCallback(() => {
    mobileHighlightRef.current = {
      active: false,
      pageNumber: null,
      items: new Map(),
      textParts: []
    };
  }, []);

  const commitMobileHighlightGesture = useCallback(() => {
    const gesture = mobileHighlightRef.current;
    if (!gesture.pageNumber || gesture.items.size === 0) {
      setSelectionPreview({ pageNumber: null, rects: [] });
      resetMobileHighlightGesture();
      return;
    }

    const rects = Array.from(gesture.items.values());
    const selectedText = gesture.textParts.join(' ').trim().slice(0, 500);
    const nextHighlight = {
      id: `pdf_h_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
      color: highlightColorId,
      text: selectedText,
      rects
    };

    setAnnotations((previous) => {
      const pageKey = String(gesture.pageNumber);
      const pageEntry = getPageEntry(previous, gesture.pageNumber);
      return {
        ...previous,
        [pageKey]: {
          ...pageEntry,
          highlights: [...pageEntry.highlights, nextHighlight]
        }
      };
    });
    setActivePageNumber(gesture.pageNumber);
    setIsDirty(true);
    setSaveError('');
    setSelectionPreview({ pageNumber: null, rects: [] });
    resetMobileHighlightGesture();
  }, [highlightColorId, resetMobileHighlightGesture]);

  const handleMobileHighlightPointerDown = useCallback((targetPageNumber) => (event) => {
    if (!isMobile || activeTool !== 'highlight') {
      return;
    }

    const pageEl = pageRefs.current[targetPageNumber];
    const targetElement = getSelectableTextElementFromPoint(event.clientX, event.clientY, pageEl);
    if (!targetElement) {
      return;
    }

    event.preventDefault();
    setActivePageNumber(targetPageNumber);
    mobileHighlightRef.current = {
      active: true,
      pageNumber: targetPageNumber,
      items: new Map(),
      textParts: []
    };
    appendMobileHighlightElement(targetElement, targetPageNumber);
  }, [activeTool, appendMobileHighlightElement, getSelectableTextElementFromPoint, isMobile]);

  const handleMobileHighlightPointerMove = useCallback((targetPageNumber) => (event) => {
    const gesture = mobileHighlightRef.current;
    if (!isMobile || activeTool !== 'highlight' || !gesture.active || gesture.pageNumber !== targetPageNumber) {
      return;
    }

    event.preventDefault();
    const pageEl = pageRefs.current[targetPageNumber];
    const targetElement = getSelectableTextElementFromPoint(event.clientX, event.clientY, pageEl);
    if (targetElement) {
      appendMobileHighlightElement(targetElement, targetPageNumber);
    }
  }, [activeTool, appendMobileHighlightElement, getSelectableTextElementFromPoint, isMobile]);

  const handleMobileHighlightPointerUp = useCallback((targetPageNumber) => () => {
    const gesture = mobileHighlightRef.current;
    if (!isMobile || activeTool !== 'highlight' || !gesture.active || gesture.pageNumber !== targetPageNumber) {
      return;
    }
    commitMobileHighlightGesture();
  }, [activeTool, commitMobileHighlightGesture, isMobile]);

  const handleDrawPointerUp = useCallback((targetPageNumber) => () => {
    const completedPoints = [...liveStrokePointsRef.current];
    const { strokeId, pageNumber: drawPageNumber } = drawStateRef.current;
    drawStateRef.current = { active: false, strokeId: null, pageNumber: null };
    liveStrokePointsRef.current = [];

    if (drawFrameRef.current) {
      window.cancelAnimationFrame(drawFrameRef.current);
      drawFrameRef.current = null;
    }

    setLiveStrokeState({ pageNumber: null, path: '' });

    if (
      activeTool !== 'draw' ||
      drawPageNumber !== targetPageNumber ||
      !strokeId ||
      completedPoints.length < 2
    ) {
      return;
    }

    updatePage(targetPageNumber, (pageEntry) => ({
      ...pageEntry,
      strokes: [
        ...pageEntry.strokes,
        {
          id: strokeId,
          createdAt: Date.now(),
          color: drawColorId,
          points: completedPoints
        }
      ]
    }));
  }, [activeTool, drawColorId, updatePage]);

  useEffect(() => {
    if (activeTool !== 'draw') {
      drawStateRef.current = { active: false, strokeId: null, pageNumber: null };
      liveStrokePointsRef.current = [];
      setLiveStrokeState({ pageNumber: null, path: '' });
      if (drawFrameRef.current) {
        window.cancelAnimationFrame(drawFrameRef.current);
        drawFrameRef.current = null;
      }
    }
  }, [activeTool]);

  useEffect(() => {
    setSelectionPreview({ pageNumber: null, rects: [] });
    resetMobileHighlightGesture();
  }, [activeTool, resetMobileHighlightGesture]);

  useEffect(() => () => {
    if (drawFrameRef.current) {
      window.cancelAnimationFrame(drawFrameRef.current);
    }
  }, []);

  const clearCurrentPageAnnotations = () => {
    updateCurrentPage(() => createEmptyPageEntry());
  };

  const undoLastAction = () => {
    updateCurrentPage((pageEntry) => {
      const highlights = [...(pageEntry.highlights || [])];
      const strokes = [...(pageEntry.strokes || [])];
      const lastHighlight = highlights[highlights.length - 1];
      const lastStroke = strokes[strokes.length - 1];
      const highlightAt = Number(lastHighlight?.createdAt || 0);
      const strokeAt = Number(lastStroke?.createdAt || 0);

      if (!lastHighlight && !lastStroke) {
        return pageEntry;
      }

      if (strokeAt >= highlightAt) {
        strokes.pop();
      } else {
        highlights.pop();
      }

      return {
        ...pageEntry,
        highlights,
        strokes
      };
    });
  };

  const exportAnnotatedPdf = async () => {
    try {
      setIsExporting(true);
      const response = await fetch(pdfUrl);
      const existingPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();

      Object.entries(annotations).forEach(([pageKey, pageEntry]) => {
        const pageIndex = Number.parseInt(pageKey, 10) - 1;
        if (!Number.isInteger(pageIndex) || pageIndex < 0 || pageIndex >= pages.length) {
          return;
        }

        const page = pages[pageIndex];
        const width = page.getWidth();
        const height = page.getHeight();

        (pageEntry.highlights || []).forEach((highlight) => {
          const highlightColor = getHighlightColor(highlight.color);
          (highlight.rects || []).forEach((rect) => {
            page.drawRectangle({
              x: rect.leftPct * width,
              y: height - ((rect.topPct + rect.heightPct) * height),
              width: rect.widthPct * width,
              height: rect.heightPct * height,
              color: highlightColor.pdf,
              opacity: 0.42,
              borderWidth: 0
            });
          });
        });

        (pageEntry.strokes || []).forEach((stroke) => {
          const strokeColor = getDrawColor(stroke.color);
          for (let index = 1; index < stroke.points.length; index += 1) {
            const previousPoint = stroke.points[index - 1];
            const currentPoint = stroke.points[index];
            page.drawLine({
              start: {
                x: previousPoint.xPct * width,
                y: height - (previousPoint.yPct * height)
              },
              end: {
                x: currentPoint.xPct * width,
                y: height - (currentPoint.yPct * height)
              },
              thickness: 2,
              color: strokeColor.pdf,
              opacity: 0.95
            });
          }
        });
      });

      const bytes = await pdfDoc.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${storageKey || 'documento'}-anotado.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } finally {
      setIsExporting(false);
    }
  };

  const saveStatusLabel = useMemo(() => {
    if (isSaving) {
      return 'Salvando ajustes...';
    }
    if (saveError) {
      return 'Falha ao salvar. Vamos tentar de novo.';
    }
    if (isDirty) {
      return 'Ajustes pendentes.';
    }
    if (lastSavedAt) {
      return 'Ajustes salvos automaticamente.';
    }
    return 'Edite e continue.';
  }, [isDirty, isSaving, lastSavedAt, saveError]);

  const renderPage = (targetPageNumber) => {
    const pageMetric = getPageMetric(targetPageNumber);
    const hasNativeText = pageMetric.hasNativeText;
    const ocrData = pageMetric.ocrData;
    const ocrLoading = pageMetric.ocrLoading;
    const ocrError = pageMetric.ocrError;
    const renderHeight = getRenderHeight(targetPageNumber);
    const ocrScale = getOcrScale(targetPageNumber);
    const pageEntry = getPageEntry(annotations, targetPageNumber);
    const pageHighlights = pageEntry.highlights || [];
    const pageStrokes = pageEntry.strokes || [];
    const strokePaths = pageStrokes.map((stroke) => ({
      id: stroke.id,
      color: getDrawColor(stroke.color).stroke,
      d: createStrokePath(stroke.points || [], renderWidth || 720, renderHeight)
    }));
    const isActivePage = effectiveActivePage === targetPageNumber;
    const showSelectionPreview =
      selectionPreview.pageNumber === targetPageNumber && selectionPreview.rects.length > 0;
    const showLiveStroke =
      liveStrokeState.pageNumber === targetPageNumber && Boolean(liveStrokeState.path);

    return (
      <Box
        key={targetPageNumber}
        data-pdf-page-number={targetPageNumber}
        position="relative"
        width={`${renderWidth || 720}px`}
        maxW="100%"
        mx="auto"
        mb={continuousMode ? 6 : 0}
        borderRadius="18px"
        boxShadow={continuousMode ? '0 14px 32px rgba(15,23,42,0.12)' : 'none'}
        outline={continuousMode && isActivePage ? '2px solid rgba(201,162,76,0.85)' : 'none'}
        bg="white"
        onPointerDown={() => setActivePageNumber(targetPageNumber)}
        onPointerDownCapture={handleMobileHighlightPointerDown(targetPageNumber)}
        onPointerMoveCapture={handleMobileHighlightPointerMove(targetPageNumber)}
        onPointerUpCapture={handleMobileHighlightPointerUp(targetPageNumber)}
        onPointerCancelCapture={handleMobileHighlightPointerUp(targetPageNumber)}
        ref={(node) => {
          if (node) {
            pageRefs.current[targetPageNumber] = node;
          } else {
            delete pageRefs.current[targetPageNumber];
          }
        }}
      >
        {continuousMode && !isMobile ? (
          <Text
            position="absolute"
            top={3}
            right={4}
            zIndex={7}
            fontSize="xs"
            fontWeight="semibold"
            color={isActivePage ? '#8A6A12' : 'gray.500'}
            bg={isActivePage ? 'rgba(255,248,220,0.92)' : 'rgba(255,255,255,0.92)'}
            px={2}
            py={1}
            borderRadius="999px"
          >
            Pagina {targetPageNumber}{isActivePage ? ' ativa' : ''}
          </Text>
        ) : null}

        <Page
          pageNumber={targetPageNumber}
          width={renderWidth || 720}
          renderTextLayer
          renderAnnotationLayer={false}
          onLoadSuccess={handlePageLoadSuccess(targetPageNumber)}
          loading={
            <Flex align="center" justify="center" minH={viewerHeight || '600px'}>
              <Spinner size="md" color="gray.400" />
            </Flex>
          }
        />

        {pageHighlights.length > 0 ? (
          <Box className="igm-pdf-highlight-overlay">
            {pageHighlights.map((highlight) =>
              highlight.rects.map((rect, index) => (
                <Box
                  key={`${highlight.id}-${index}`}
                  className="igm-pdf-highlight-rect"
                  style={{
                    background: getHighlightColor(highlight.color).fill,
                    left: `${rect.leftPct * 100}%`,
                    top: `${rect.topPct * 100}%`,
                    width: `${rect.widthPct * 100}%`,
                    height: `${rect.heightPct * 100}%`
                  }}
                />
              ))
            )}
          </Box>
        ) : null}

        {showSelectionPreview ? (
          <Box className="igm-pdf-highlight-overlay">
            {selectionPreview.rects.map((rect, index) => (
              <Box
                key={`preview-${targetPageNumber}-${index}`}
                className="igm-pdf-highlight-rect"
                style={{
                  background: activeHighlightColor.fill,
                  opacity: 0.9,
                  left: `${rect.leftPct * 100}%`,
                  top: `${rect.topPct * 100}%`,
                  width: `${rect.widthPct * 100}%`,
                  height: `${rect.heightPct * 100}%`
                }}
              />
            ))}
          </Box>
        ) : null}

        {strokePaths.length > 0 || showLiveStroke || activeTool === 'draw' ? (
          <svg
            className={`igm-pdf-draw-overlay ${activeTool === 'draw' && isActivePage ? 'is-drawing' : ''}`}
            width={renderWidth || 720}
            height={renderHeight}
            viewBox={`0 0 ${renderWidth || 720} ${renderHeight}`}
            onPointerDown={handleDrawPointerDown(targetPageNumber)}
            onPointerMove={handleDrawPointerMove(targetPageNumber)}
            onPointerUp={handleDrawPointerUp(targetPageNumber)}
            onPointerLeave={handleDrawPointerUp(targetPageNumber)}
            onPointerCancel={handleDrawPointerUp(targetPageNumber)}
          >
            {strokePaths.map((stroke) => (
              <path
                key={stroke.id}
                d={stroke.d}
                fill="none"
                stroke={stroke.color}
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
            {showLiveStroke ? (
              <path
                d={liveStrokeState.path}
                fill="none"
                stroke={activeDrawColor.stroke}
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}
          </svg>
        ) : null}

        {!hasNativeText && ocrLoading ? (
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            bg="rgba(255,255,255,0.7)"
            zIndex={2}
          >
            <Spinner size="sm" color="gray.500" mr={2} />
            <Text fontSize="sm" color="gray.600">Preparando OCR...</Text>
          </Flex>
        ) : null}

        {!hasNativeText && ocrError ? (
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            bg="rgba(255,255,255,0.8)"
            zIndex={2}
          >
            <Text fontSize="sm" color="red.500">{ocrError}</Text>
          </Flex>
        ) : null}

        {!hasNativeText && ocrData?.words ? (
          <Box
            className="igm-ocr-layer"
            position="absolute"
            left={0}
            top={0}
            width={`${renderWidth || 720}px`}
            height={`${ocrData.height * ocrScale}px`}
            zIndex={1}
          >
            {ocrData.words.map((word, index) => {
              const left = word.x * ocrScale;
              const top = word.y * ocrScale;
              const width = word.w * ocrScale;
              const height = word.h * ocrScale;
              return (
                <span
                  key={`${targetPageNumber}-${word.text}-${index}`}
                  className="igm-ocr-word"
                  style={{
                    left: `${left}px`,
                    top: `${top}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                    fontSize: `${Math.max(6, height)}px`
                  }}
                >
                  {word.text}
                </span>
              );
            })}
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <Box
      bg="white"
      borderRadius={{ base: '18px', md: '24px' }}
      boxShadow={{ base: '0 12px 28px rgba(15,23,42,0.1)', md: '0 24px 60px rgba(15,23,42,0.18)' }}
      overflow="hidden"
      px={{ base: 1, md: 4 }}
      py={{ base: 2, md: 4 }}
    >
      {annotationMode ? (
        isMobile ? (
          <Flex
            align="center"
            justify="space-between"
            gap={2}
            mb={2}
            px={1}
            wrap="wrap"
          >
            <VStack align="start" spacing={0} maxW="60%">
              <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                Leitura e anotacao
              </Text>
              <Text fontSize="11px" color={saveError ? 'red.500' : 'green.600'} fontWeight="medium">
                {saveStatusLabel}
              </Text>
            </VStack>
            <Flex align="center" gap={2}>
              <Button
                size="xs"
                variant="outline"
                colorScheme="blue"
                className="igm-pdf-header-action"
                onClick={() => persistAnnotations(annotations)}
                isLoading={isSaving}
                leftIcon={<FaSave />}
              >
                Salvar
              </Button>
              <Button
                size="xs"
                colorScheme="blue"
                className="igm-pdf-header-action"
                onClick={exportAnnotatedPdf}
                isLoading={isExporting}
                leftIcon={isDirty ? <FaDownload /> : <FaCheck />}
              >
                Copia
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={3} mb={3} wrap="wrap">
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Editor do PDF
              </Text>
              <Text fontSize="xs" color="gray.500">
                {continuousMode
                  ? 'Role o documento livremente. Marca-texto e pincel atuam na pagina ativa.'
                  : 'Toque em marca-texto ou pincel. Seus ajustes ficam salvos automaticamente.'}
              </Text>
            </VStack>
            <VStack align={{ base: 'start', md: 'end' }} spacing={1}>
              <Text fontSize="xs" color={saveError ? 'red.500' : 'green.600'} fontWeight="medium">
                {saveStatusLabel}
              </Text>
              {continuousMode ? (
                <Text fontSize="11px" color="gray.500">
                  Pagina ativa: {effectiveActivePage}
                </Text>
              ) : null}
              {lastSavedAt && !saveError ? (
                <Text fontSize="11px" color="gray.400">
                  {new Date(lastSavedAt).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Text>
              ) : null}
            </VStack>
          </Flex>
        )
      ) : null}

      <Box position="relative">
        <Box
          ref={(node) => {
            measureRef.current = node;
            viewerRef.current = node;
          }}
          data-pdf-annotation-root="true"
          position="relative"
          width="100%"
          maxW={viewerMaxWidth ? `${viewerMaxWidth}px` : '100%'}
          mx="auto"
          pb={annotationMode ? { base: '112px', md: '20px' } : 0}
          className={[
            activeTool === 'highlight' ? 'igm-pdf-markup-enabled' : '',
            isMobile && activeTool === 'highlight' ? 'igm-pdf-mobile-highlight-enabled' : ''
          ].filter(Boolean).join(' ')}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={handleDocumentLoadSuccess}
            renderMode="canvas"
            loading={
              <Flex align="center" justify="center" minH={viewerHeight || '600px'}>
                <Spinner size="lg" color="blue.500" />
              </Flex>
            }
          >
            {pageNumbers.map(renderPage)}
          </Document>
        </Box>
      </Box>
      {annotationMode ? (
        <Box
          data-pdf-annotation-root="true"
          data-highlighter-control="true"
          className={`igm-pdf-toolbar ${isMobile ? 'is-mobile' : 'is-desktop'} is-floating`}
          role="toolbar"
          aria-label="Ferramentas do PDF"
        >
          <Button
            size={isMobile ? 'md' : 'sm'}
            className={`igm-pdf-tool ${activeTool === 'none' ? 'is-active is-nav' : ''}`}
            onClick={() => setActiveTool('none')}
            leftIcon={<FaHandPaper />}
          >
            Mover
          </Button>
          <Button
            size={isMobile ? 'md' : 'sm'}
            className={`igm-pdf-tool ${activeTool === 'highlight' ? 'is-active' : ''}`}
            onClick={() => setActiveTool('highlight')}
            leftIcon={<FaHighlighter />}
          >
            {isMobile ? 'Texto' : 'Marca-texto'}
          </Button>
          {activeTool === 'highlight' ? (
            <Flex align="center" gap={2} className="igm-pdf-color-group">
              {HIGHLIGHT_COLORS.map((color) => (
                <button
                  key={color.id}
                  type="button"
                  className={`igm-pdf-color-dot ${highlightColorId === color.id ? 'is-active' : ''}`}
                  style={{ background: color.fill }}
                  onClick={() => setHighlightColorId(color.id)}
                  aria-label={`Usar marca ${color.id}`}
                />
              ))}
            </Flex>
          ) : null}
          <Button
            size={isMobile ? 'md' : 'sm'}
            className={`igm-pdf-tool ${activeTool === 'draw' ? 'is-active is-draw' : ''}`}
            onClick={() => setActiveTool('draw')}
            leftIcon={<FaPaintBrush />}
          >
            {isMobile ? 'Pincel' : (activeTool === 'draw' ? 'Pincel ON' : 'Pincel')}
          </Button>
          {activeTool === 'draw' ? (
            <Flex align="center" gap={2} className="igm-pdf-color-group">
              {DRAW_COLORS.map((color) => (
                <button
                  key={color.id}
                  type="button"
                  className={`igm-pdf-color-dot ${drawColorId === color.id ? 'is-active' : ''}`}
                  style={{ background: color.stroke }}
                  onClick={() => setDrawColorId(color.id)}
                  aria-label={`Usar pincel ${color.id}`}
                />
              ))}
            </Flex>
          ) : null}
          {isMobile ? (
            <Button
              size="md"
              variant="solid"
              className="igm-pdf-tool igm-pdf-clear-tool"
              onClick={clearCurrentPageAnnotations}
              isDisabled={currentHighlights.length === 0 && currentStrokes.length === 0}
              leftIcon={<FaTrash />}
            >
              Limpar
            </Button>
          ) : null}
          <Button
            size={isMobile ? 'md' : 'sm'}
            variant="ghost"
            className="igm-pdf-tool"
            onClick={undoLastAction}
            isDisabled={currentHighlights.length === 0 && currentStrokes.length === 0}
            leftIcon={<FaUndo />}
          >
            Desfazer
          </Button>
          {!isMobile ? (
            <Button
              size="sm"
              variant="ghost"
              className="igm-pdf-tool"
              onClick={clearCurrentPageAnnotations}
              isDisabled={currentHighlights.length === 0 && currentStrokes.length === 0}
              leftIcon={<FaTrash />}
            >
              Limpar
            </Button>
          ) : null}
          {!isMobile ? (
            <Button
              size="sm"
              colorScheme="blue"
              className="igm-pdf-tool igm-pdf-save-tool"
              onClick={() => persistAnnotations(annotations)}
              isLoading={isSaving}
              leftIcon={<FaSave />}
            >
              Salvar
            </Button>
          ) : null}
          {!isMobile ? (
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              className="igm-pdf-tool"
              onClick={exportAnnotatedPdf}
              isLoading={isExporting}
              leftIcon={isDirty ? <FaDownload /> : <FaCheck />}
            >
              Copia
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default PdfOcrViewer;
